import React from "react";
import styled from "styled-components";
import { Menu, Drawer, Avatar, Button, Dropdown, Message } from "@arco-design/web-react";
import {
  IconHome,
  IconUserAdd,
  IconNotification,
  IconUserGroup,
  IconStorage,
  IconList,
  IconFilter,
  IconCopy,
  IconFolder,
  IconClockCircle,
  IconApps,
  IconDashboard,
  IconSettings,
  IconMenu,
  IconPoweroff,
  IconSun,
  IconMoon,
  IconPlus,
  IconCheck,
  IconEye,
  IconSafe,
  IconUser,
} from "@arco-design/web-react/icon";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { FetchApi, PostApi } from "../Api/AuthenticatedApi";
import { avatarInitial } from "./avatarInitial";
import OrganizationsDialog from "./OrganizationsDialog";
import useOrgFeatures from "../utils/useOrgFeatures";
import useMyUser, { setMyUser } from "../Account/myUser";

export const drawerWidth = 240;
// From this width up the sidebar stays on screen. Below it the sidebar would
// leave too little room for the page, so it opens as a drawer instead.
export const desktopWidth = 900;

// MUI -> Arco. The MUI AppBar + responsive Drawer (permanent on desktop /
// temporary on mobile) + List nav become a styled header, an Arco Menu, a fixed
// desktop sidebar, and an Arco Drawer on mobile. makeStyles -> styled-components;
// theme palette values are inlined.

// The sidebar, in groups. A group with no title sits at the top, unlabeled.
const NAV_GROUPS = [
  {
    items: [
      { label: "Home", text: "home", icon: <IconHome /> },
      { label: "Reports", text: "reports", icon: <IconDashboard /> },
    ],
  },
  {
    title: "Audience",
    items: [
      { label: "Signups", text: "signups", icon: <IconUserAdd /> },
      { label: "Contacts", text: "contacts", icon: <IconUserGroup /> },
      { label: "Companies", text: "companies", icon: <IconStorage />, feature: "companies" },
      { label: "Contact Lists", text: "lists", icon: <IconList /> },
      { label: "Segments", text: "segments", icon: <IconFilter /> },
    ],
  },
  {
    title: "Sending",
    items: [
      { label: "Broadcasts", text: "broadcasts", icon: <IconNotification /> },
      { label: "Sequences", text: "sequences", icon: <IconClockCircle /> },
      { label: "Workflows", text: "workflows", icon: <IconApps />, feature: "workflows" },
    ],
  },
  {
    title: "Resources",
    items: [
      { label: "Templates", text: "templates", icon: <IconCopy /> },
      { label: "File Assets", text: "file-assets", icon: <IconFolder /> },
      { label: "Email Spy", text: "spy", icon: <IconEye /> },
    ],
  },
];
// An item with a `feature` shows only when the organization has turned that
// feature on under Settings (see useOrgFeatures).
// Settings is deliberately absent: it lives in the avatar menu, next to the
// organization it applies to. The sidebar is the places you work; the avatar
// menu is the account and its settings.

const Root = styled.div`
  display: flex;
`;

const HeaderBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  /* Fills the row. In a flex row it would otherwise shrink to its contents. */
  flex: 1 1 auto;
  min-width: 0;
  height: 64px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  background: var(--color-bg-2);
  border-bottom: 1px solid var(--color-border-2);
  @media (min-width: ${desktopWidth}px) {
    width: calc(100% - ${drawerWidth}px);
    margin-left: ${drawerWidth}px;
    padding: 0 20px;
  }
`;

// Brand lockup, used in the header (mobile) + the sidebar top.
const Brand = styled(RouterLink)`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--color-text-1);
  text-decoration: none;
  font-size: 19px;
  font-weight: 600;
  white-space: nowrap;
  letter-spacing: -0.01em;
`;

const BrandMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgb(var(--arcoblue-6));
  color: #fff;
  font-size: 16px;
`;

const MenuButton = styled(Button)`
  margin-right: 8px;
  @media (min-width: ${desktopWidth}px) {
    display: none;
  }
`;

// The brand shows in the header on mobile (the sidebar carries it on desktop).
const MobileBrand = styled.div`
  @media (min-width: ${desktopWidth}px) {
    display: none;
  }
`;

// Permanent sidebar on desktop; hidden on mobile (the Drawer is used instead).
const Sidebar = styled.nav`
  display: none;
  @media (min-width: ${desktopWidth}px) {
    display: block;
    width: ${drawerWidth}px;
    flex-shrink: 0;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    background: var(--color-bg-2);
    border-right: 1px solid var(--color-border-2);
    z-index: 101;
  }
`;

function NavMenu({ pathname, onSelect }) {
  const features = useOrgFeatures();
  const groups = NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((i) => !i.feature || features[i.feature]) }))
    .filter((g) => g.items.length > 0);
  const selectedKey = groups.flatMap((g) => g.items).find((i) => pathname === `/${i.text}`)?.text;
  const renderItem = ({ label, text, icon }) => (
    <Menu.Item key={text} onClick={onSelect}>
      <RouterLink
        to={`/${text}`}
        style={{
          // Fill the entire Menu.Item so the whole highlighted button is
          // clickable, not just the text. The vertical Arco menu item uses
          // `padding: 0 12px; line-height: 40px`; the negative margins cancel
          // that padding while the matching padding keeps the text position
          // identical.
          display: "block",
          margin: "0 -12px",
          padding: "0 12px",
          color: "inherit",
          textDecoration: "none",
        }}
      >
        {icon} {label || text}
      </RouterLink>
    </Menu.Item>
  );
  return (
    <Menu selectedKeys={selectedKey ? [selectedKey] : []} style={{ width: "100%" }}>
      {groups.map((g, i) =>
        g.title ? (
          <Menu.ItemGroup key={g.title} title={g.title}>{g.items.map(renderItem)}</Menu.ItemGroup>
        ) : (
          <React.Fragment key={i}>{g.items.map(renderItem)}</React.Fragment>
        )
      )}
    </Menu>
  );
}

// The Contact Anchor mark: a simple line-drawn anchor. Rendered white inside the
// brass BrandMark square. Also used by the sign-in page.
export function AnchorGlyph({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="4" r="2.1" stroke="currentColor" strokeWidth="1.7" />
      <path d="M12 6.1V20" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M7 10.5h10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path
        d="M4.5 13c0 4.1 3.4 6.6 7.5 6.9C16.1 19.6 19.5 17.1 19.5 13"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function BrandLockup() {
  return (
    <Brand to="/">
      <BrandMark>
        <AnchorGlyph size={17} />
      </BrandMark>
      Contact Anchor
    </Brand>
  );
}

export default function ResponsiveDrawer() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user: authUser, getAccessTokenSilently } = useAuth0();
  // Our own copy of the user (it has the name set on the account page); the
  // Auth0 profile only until /api/me loads, and never in local-login mode.
  const myUser = useMyUser();
  const user = myUser || authUser;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const toggle = () => setMobileOpen((v) => !v);

  // Light/dark theme. The initial attribute is set by an inline script in
  // index.html (before paint); this mirrors it into state and persists changes.
  const [dark, setDark] = React.useState(
    () => typeof document !== "undefined" && document.body.getAttribute("arco-theme") === "dark"
  );
  const toggleTheme = () =>
    setDark((prev) => {
      const next = !prev;
      if (next) document.body.setAttribute("arco-theme", "dark");
      else document.body.removeAttribute("arco-theme");
      try {
        localStorage.setItem("ca-theme", next ? "dark" : "light");
      } catch (e) {
        /* private mode / storage disabled — theme just won't persist */
      }
      return next;
    });

  // The orgs this user belongs to, for the switcher. Always listed, even with
  // one: the group is where "New organization" lives, and hiding it below two
  // made multi-org support look like it didn't exist.
  const [orgs, setOrgs] = React.useState([]);
  const [activeOrgId, setActiveOrgId] = React.useState(null);
  const [orgsOpen, setOrgsOpen] = React.useState(false);
  // Platform admin, from /api/me. Only decides whether the link shows: the API
  // gates every admin route on its own allowlist regardless.
  const [platformAdmin, setPlatformAdmin] = React.useState(false);

  React.useEffect(() => {
    FetchApi(getAccessTokenSilently, "/api/me",
      (data) => {
        setOrgs((data && data.organizations) || []);
        setActiveOrgId(data && data.active_organization_id);
        setPlatformAdmin(!!(data && data.platform_admin));
        setMyUser(data && data.user);
      },
      () => {}, () => {});
  }, [getAccessTokenSilently]);

  const switchOrg = (orgId) => {
    PostApi(getAccessTokenSilently, "/api/me/active-org",
      // Full reload so every org-scoped screen refetches under the new active org.
      () => { window.location.href = "/"; },
      () => navigate("/login"),
      () => Message.error("Failed to switch organization"),
      { organization_id: orgId });
  };

  // Renaming doesn't reload, so the switcher's copy of the list is patched in
  // place — otherwise the menu would keep showing the old name until refresh.
  const applyRename = (updated) => {
    if (!updated || !updated.id) return;
    setOrgs((prev) => prev.map((o) => (o.id === updated.id ? { ...o, name: updated.name } : o)));
  };

  const handleUserMenuClick = (key) => {
    if (key === "manage-orgs") { setOrgsOpen(true); return; }
    if (key === "spy-domains") { navigate("/admin/spy-domains"); return; }
    if (key === "accounts") { navigate("/admin/accounts"); return; }
    if (key.startsWith("org:")) { switchOrg(key.slice(4)); return; }
    if (key === "logout") { navigate("/logout"); return; }
    if (key === "account") { navigate("/account"); return; }
    navigate("/settings");
  };

  const userMenu = (
    <Menu className="ca-user-menu ca-menu-fit" onClickMenuItem={handleUserMenuClick}>
      <Menu.ItemGroup title="Organization" className="ca-menu-group">
        {orgs.map((o) => (
          <Menu.Item key={"org:" + o.id}>
            <span className="ca-menu-row">
              <IconCheck className={o.id === activeOrgId ? undefined : "ca-menu-check-hidden"} />
              <span className="ca-menu-label" title={o.name}>{o.name}</span>
            </span>
          </Menu.Item>
        ))}
        <Menu.Item key="manage-orgs">
          <span className="ca-menu-row">
            <IconPlus />
            <span className="ca-menu-label">Add or rename…</span>
          </span>
        </Menu.Item>
      </Menu.ItemGroup>
      <Menu.Item key="account">
        <span className="ca-menu-row">
          <IconUser />
          <span className="ca-menu-label" title={user && user.email}>Your account</span>
        </span>
      </Menu.Item>
      <Menu.Item key="settings">
        <span className="ca-menu-row">
          <IconSettings />
          <span className="ca-menu-label">Settings</span>
        </span>
      </Menu.Item>
      {platformAdmin && (
        <Menu.Item key="spy-domains">
          <span className="ca-menu-row">
            <IconEye />
            <span className="ca-menu-label">Spy domains (admin)</span>
          </span>
        </Menu.Item>
      )}
      {platformAdmin && (
        <Menu.Item key="accounts">
          <span className="ca-menu-row">
            <IconSafe />
            <span className="ca-menu-label">Accounts (admin)</span>
          </span>
        </Menu.Item>
      )}
      <Menu.Item key="logout">
        <span className="ca-menu-row">
          <IconPoweroff />
          <span className="ca-menu-label">Log out</span>
        </span>
      </Menu.Item>
    </Menu>
  );

  return (
    <Root>
      <Sidebar>
        <div style={{ height: 64, display: "flex", alignItems: "center", padding: "0 20px", borderBottom: "1px solid var(--color-border-2)" }}>
          <BrandLockup />
        </div>
        <div style={{ padding: "8px 0" }}>
          <NavMenu pathname={pathname} />
        </div>
      </Sidebar>

      <Drawer
        width={drawerWidth}
        placement="left"
        visible={mobileOpen}
        onCancel={toggle}
        footer={null}
        title={<BrandLockup />}
        closable={false}
      >
        <NavMenu pathname={pathname} onSelect={toggle} />
      </Drawer>

      <HeaderBar>
        <MenuButton
          type="text"
          icon={<IconMenu />}
          aria-label="open drawer"
          onClick={toggle}
        />
        <MobileBrand>
          <BrandLockup />
        </MobileBrand>
        <div style={{ flexGrow: 1 }} />
        <Button
          type="text"
          shape="circle"
          aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
          icon={dark ? <IconSun /> : <IconMoon />}
          onClick={toggleTheme}
          style={{ marginRight: 12, fontSize: 18, color: "var(--color-text-2)" }}
        />
        <Dropdown droplist={userMenu} position="br" trigger="click">
          <Avatar
            size={36}
            style={{ backgroundColor: "rgb(var(--arcoblue-6))", cursor: "pointer" }}
          >
            {avatarInitial(user)}
          </Avatar>
        </Dropdown>
      </HeaderBar>

      <OrganizationsDialog
        visible={orgsOpen}
        orgs={orgs}
        activeOrgId={activeOrgId}
        onClose={() => setOrgsOpen(false)}
        onRenamed={applyRename}
      />
    </Root>
  );
}
